import type { FeatureDefinitionGroup, FeatureExtractionVisibility } from '../feature-definition-types';

export type AnalyticsRelativePath =
  | '/dashboard'
  | '/practice/main'
  | '/ai-receptionist/'
  | '/call-intelligence/main'
  | '/marketing-attribution/main'
  | '/auto-recall'
  | '/phone/main'
  | '/messaging'
  | '/appointment'
  | '/reviews'
  | '/staff-performance'
  | '/communication';

export const featureDefinition = {
  root: '/analytics',
  iconName: 'analytics',
  items: {
    '/dashboard': {
      label: 'Overview',
      trackingId: 'nav-analytics-overview',
      description:
        'High-level analytics dashboard providing snapshots of calls, messages, appointments, and other KPIs.',
    },
    '/practice/main': {
      label: 'Practice',
      trackingId: 'nav-analytics-practice',
      description:
        'In-depth metrics about the practice’s overall performance, including opportunities, patient provider production, and more.',
    },
    '/ai-receptionist/': {
      label: 'AI Receptionist',
      trackingId: 'nav-analytics-ai-receptionist',
      description: 'Detailed data on ai receptionist, scheduling and follow up tasks.',
    },
    '/call-intelligence/main': {
      label: 'Call Intelligence',
      trackingId: 'nav-analytics-call-intelligence',
      description: 'Detailed data on call performance, sentiment, call durations, and other telephony insights.',
    },
    '/marketing-attribution/main': {
      label: 'Marketing',
      trackingId: 'nav-analytics-marketing',
      description: 'Detailed data on marketing performance, attribution, ROI, and other insights.',
    },
    '/auto-recall': {
      label: 'Auto Recall',
      trackingId: 'nav-analytics-auto-recall',
      description:
        'Metrics related to the automated recall system, tracking outreach effectiveness and follow-up responses.',
    },
    '/phone/main': {
      label: 'Phone',
      trackingId: 'nav-analytics-phone',
      description: 'Analytics focused on phone usage patterns, volume, and trends for better resource management.',
    },
    '/communication': {
      label: 'Communication',
      trackingId: 'nav-analytics-communication',
      description:
        'Combined analytics for phone, messaging, and other communication channels to provide a holistic view of engagement and performance.',
    },
    '/messaging': {
      label: 'Messaging',
      trackingId: 'nav-analytics-messaging',
      description: 'Reports on message volume, response rates, and overall engagement via text or chat channels.',
    },
    '/appointment': {
      label: 'Appointment',
      trackingId: 'nav-analytics-appointments',
      description: 'Tracks appointment-related data, such as scheduling trends, cancellations, and no-show rates.',
    },
    '/reviews': {
      label: 'Reviews',
      trackingId: 'nav-analytics-reviews',
      description: 'Tracks patient or customer sentiment over time, analyzing both quantity and quality of reviews.',
    },
    '/staff-performance': {
      label: 'Staff Performance',
      trackingId: 'nav-analytics-staff-performance',
      description: 'Track and monitor staff productivity and performance metrics.',
    },
  },
} as const satisfies FeatureDefinitionGroup<'/analytics', 'analytics', AnalyticsRelativePath>;

export const featureExtractionVisibility = {
  itemShowClassifications: {
    '/practice/main': 'runtime-conditional',
    '/ai-receptionist/': 'runtime-conditional',
    '/call-intelligence/main': 'runtime-conditional',
    '/marketing-attribution/main': 'runtime-conditional',
    '/auto-recall': 'runtime-conditional',
    '/appointment': 'runtime-conditional',
    '/reviews': 'runtime-conditional',
    '/staff-performance': 'runtime-conditional',
  },
} as const satisfies FeatureExtractionVisibility<AnalyticsRelativePath>;
